import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Send, Lock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { renderContent } from "@/components/AIAssistant";

const FREE_QUESTIONS = 3;
const PROMPTS = ["Suggest 3 taglines for a home bakery", "Build a cream & blush brand palette", "What do I need to start an LLC?"];

export default function TeaserChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const used = messages.filter((m) => m.role === "user").length;
  const exhausted = used >= FREE_QUESTIONS;

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy || exhausted) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: message }]);
    try {
      const { data } = await api.post("/ai/teaser", { message });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: formatApiError(e) }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass rounded-2xl flex flex-col overflow-hidden" data-testid="teaser-chat">
      <div className="flex items-center justify-between px-5 h-14 border-b border-[#E8DCCB] bg-[#FFF3F8]">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-lg bg-[#FFFDF7] border border-[#D4A0B4] flex items-center justify-center">
            <Sparkles size={15} className="text-[#A8785A]" />
          </span>
          <p className="f-display font-medium text-sm leading-none">Copilot Preview</p>
        </div>
        <span className="f-mono text-[10px] text-[#8C7562]" data-testid="teaser-remaining">{Math.max(FREE_QUESTIONS - used, 0)} FREE LEFT</span>
      </div>
      <div className="px-5 py-5 flex flex-col gap-3 min-h-[220px] max-h-[380px] overflow-y-auto" data-testid="teaser-messages">
        {messages.length === 0 && (
          <p className="text-sm text-[#8C7562] leading-relaxed">Pick a prompt or type your own. No account needed for the preview.</p>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`max-w-[90%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
            m.role === "user" ? "self-end bg-[#A8785A] text-[#FFFDF7] rounded-br-md" : "self-start bg-[#FFFDF7] border border-[#E8DCCB] rounded-bl-md"}`}>
            {m.role === "assistant" ? renderContent(m.content) : m.content}
          </div>
        ))}
        {busy && <span className="self-start f-mono text-[10px] text-[#C48A9F] animate-pulse">THINKING…</span>}
        {exhausted && !busy && (
          <div className="self-stretch rounded-xl border border-[#D4A0B4] bg-[#FFF3F8] p-4 text-sm" data-testid="teaser-exhausted">
            <p className="font-bold flex items-center gap-2"><Lock size={13} /> That's your three free questions.</p>
            <p className="text-[#8C7562] mt-1">Unlock The Organizer for unlimited copilot chats that read your organizer and fill it in for you.</p>
            <Link to="/organizer" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="btn-primary !py-2 !px-4 !text-xs mt-3" data-testid="teaser-unlock-btn">Unlock for $49</Link>
          </div>
        )}
      </div>
      {!exhausted && (
        <>
          <div className="px-5 pb-2 flex flex-wrap gap-2">
            {PROMPTS.map((q) => (
              <button key={q} onClick={() => send(q)} data-testid="teaser-quick-prompt"
                className="text-[11px] font-semibold border border-[#E8DCCB] rounded-full px-3 py-1.5 text-[#8C7562] hover:border-[#A8785A] hover:text-[#A8785A] transition-colors">
                {q}
              </button>
            ))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-4 border-t border-[#E8DCCB] flex gap-2">
            <input className="field !py-2.5" placeholder="Ask a business question…" value={input}
              onChange={(e) => setInput(e.target.value)} maxLength={500} data-testid="teaser-input" />
            <button type="submit" disabled={busy} data-testid="teaser-send-btn"
              className="w-11 h-11 rounded-xl bg-[#A8785A] text-[#FFFDF7] flex items-center justify-center shrink-0 hover:bg-[#3A2E25] transition-colors disabled:opacity-50">
              <Send size={16} />
            </button>
          </form>
        </>
      )}
    </div>
  );
}
